/**
 * MathLine.js
 * Mathematical line calculations
 */

import { MATHLINE_NEAR_ZERO_PRECISION } from "../../Constants";

export class MathLine {
    /**
     * @public
     * @param {Number} p1x
     * @param {Number} p1y
     * @param {Number} p2x
     * @param {Number} p2y
     */
    constructor(p1x, p1y, p2x, p2y) {
        /** @private @type {Number} - Line extremities */
        this._p1X = 0;
        /** @private @type {Number} */
        this._p1Y = 0;
        /** @private @type {Number} */
        this._p2X = 0;
        /** @private @type {Number} */
        this._p2Y = 0;

        /** @private @type {Number} */
        this._xMin = 0;
        /** @private @type {Number} */
        this._xMax = 0;

        /** @private @type {Number} */
        this._yMin = 0;
        /** @private @type {Number} */
        this._yMax = 0;

        /** @private @type {Number} */
        this._segmentDeltaX = 0;
        /** @private @type {Number} */
        this._segmentDeltaY = 0;

        // Line equation : y = slope * x + yIntercept
        /** @private @type {Number} - Slope */
        this._slope = 0;
        /** @private @type {Number} - Y Intercept */
        this._yIntercept = 0;

        /** @private @type {Number} - Length of the line segment */
        this._length = 0;

        /** @private @type {Number} - Inverse square length of the line segment */
        this._inverseSquareLength = 0;

        this.reset(p1x, p1y, p2x, p2y);
    }

    /**
     * @public
     * @param {Number} p1x
     * @param {Number} p1y
     * @param {Number} p2x
     * @param {Number} p2y
     */
    reset(p1x, p1y, p2x, p2y) {
        this._p1X = p1x;
        this._p1Y = p1y;
        this._p2X = p2x;
        this._p2Y = p2y;

        this._xMin = p1x < p2x ? p1x : p2x;
        this._xMax = p1x < p2x ? p2x : p1x;
        this._yMin = p1y < p2y ? p1y : p2y;
        this._yMax = p1y < p2y ? p2y : p1y;

        this._segmentDeltaX = p2x - p1x;
        this._segmentDeltaY = p2y - p1y;

        if (Math.abs(this._segmentDeltaX) < MATHLINE_NEAR_ZERO_PRECISION) {
            this._slope = Infinity;
            this._segmentDeltaX = 0;
            this._yIntercept = 0;
        } else {
            this._slope = this._segmentDeltaY / this._segmentDeltaX;
            this._yIntercept = this._p1Y - this._slope * this._p1X;
        }

        const squareLength = this._segmentDeltaX * this._segmentDeltaX + this._segmentDeltaY * this._segmentDeltaY;
        this._length = Math.sqrt(squareLength);
        this._inverseSquareLength = squareLength > 0 ? 1 / squareLength : Infinity;
    }

    /**
     * @public
     * @returns {Number}
     */
    get xMin() {
        return this._xMin;
    }

    /**
     * @public
     * @returns {Number}
     */
    get xMax() {
        return this._xMax;
    }

    /**
     * @public
     * @returns {Number}
     */
    get yMin() {
        return this._yMin;
    }

    /**
     * @public
     * @returns {Number}
     */
    get yMax() {
        return this._yMax;
    }

    /**
     * @public
     * @returns {Number}
     */
    get p1X() {
        return this._p1X;
    }

    /**
     * @public
     * @returns {Number}
     */
    get segmentDeltaX() {
        return this._segmentDeltaX;
    }

    /**
     * @public
     * @returns {Number}
     */
    get segmentDeltaY() {
        return this._segmentDeltaY;
    }

    /**
     * @public
     * @returns {Number}
     */
    get slope() {
        return this._slope;
    }

    /**
     * @public
     * @returns {Number}
     */
    get yIntercept() {
        return this._yIntercept;
    }

    /**
     * @public
     * @returns {Number}
     */
    get length() {
        return this._length;
    }

    /**
     * Calculates the intersection of this line with another line
     * @param {MathLine} otherline
     * @returns {{x: Number, y: Number}}
     */
    intersection(otherline) {
        // Result intersection point
        let intersectionPt = null;

        const precision = MATHLINE_NEAR_ZERO_PRECISION;

        // Line has infinite slope (constant x)
        if (this._segmentDeltaX === 0) {
            if (otherline.segmentDeltaX === 0) {
                // Lines are parallel ==> no intersection
                return null;
            } else {
                // Calculate intersection
                intersectionPt = {
                    x: this._p1X,
                    y: otherline.slope * this._p1X + otherline.yIntercept
                };
                if (
                    intersectionPt.x >= this._xMin - precision &&
                    intersectionPt.x <= this._xMax + precision &&
                    intersectionPt.y >= this._yMin - precision &&
                    intersectionPt.y <= this._yMax + precision &&
                    intersectionPt.x >= otherline.xMin - precision &&
                    intersectionPt.x <= otherline.xMax + precision &&
                    intersectionPt.y >= otherline.yMin - precision &&
                    intersectionPt.y <= otherline.yMax + precision
                ) {
                    return intersectionPt;
                }

                // No intersection
                return null;
            }
        }

        // Lines are parallel ==> no intersection
        if (this._slope === otherline.slope) {
            return null;
        }

        // Other line has infinite slope (constant x)
        if (otherline.segmentDeltaY === 0) {
            // Calculate intersection
            intersectionPt = {
                x: otherline.p1X,
                y: this._slope * otherline.p1X + this._yIntercept
            };
            if (
                intersectionPt.x >= this._xMin - precision &&
                intersectionPt.x <= this._xMax + precision &&
                intersectionPt.y >= this._yMin - precision &&
                intersectionPt.y <= this._yMax + precision &&
                intersectionPt.x >= otherline.xMin - precision &&
                intersectionPt.x <= otherline.xMax + precision &&
                intersectionPt.y >= otherline.yMin - precision &&
                intersectionPt.y <= otherline.yMax + precision
            ) {
                return intersectionPt;
            }

            // No intersection
            return null;
        }

        // Calculate intersection
        const intersectionX = (this._yIntercept - otherline.yIntercept) / (otherline.slope - this._slope);
        intersectionPt = {
            x: intersectionX,
            y: this._slope * intersectionX + this._yIntercept
        };
        if (
            intersectionPt.x >= this._xMin - precision &&
            intersectionPt.x <= this._xMax + precision &&
            intersectionPt.y >= this._yMin - precision &&
            intersectionPt.y <= this._yMax + precision &&
            intersectionPt.x >= otherline.xMin - precision &&
            intersectionPt.x <= otherline.xMax + precision &&
            intersectionPt.y >= otherline.yMin - precision &&
            intersectionPt.y <= otherline.yMax + precision
        ) {
            return intersectionPt;
        }

        // No intersection
        return null;
    }

    /**
     * Calculates the distance from this line to the specified point
     * @public
     * @param {Number} x
     * @param {Number} y
     * @returns {Number}
     */
    distanceToPoint(x, y) {
        let deltaX = 0;
        let deltaY = 0;

        // Line segment is a single point
        if (this._length === 0) {
            deltaX = x - this._p1X;
            deltaY = y - this._p1Y;
            return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        }

        // Segment ratio : 0 -> 1 ratio on the line segment perpendicular to the specified point
        deltaX = x - this._p1X;
        deltaY = y - this._p1Y;
        const segmentRatio = (deltaX * this._segmentDeltaX + deltaY * this._segmentDeltaY) * this._inverseSquareLength;

        if (segmentRatio < 0) {
            // Specified point is 'outside' the line segment on the side of the P1 extremity
            return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        } else if (segmentRatio > 1) {
            // Specified point is 'outside' the line segment on the side of the P2 extremity
            deltaX = x - this._p2X;
            deltaY = y - this._p2Y;
            return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        } else {
            const intersectionX = this._p1X + segmentRatio * this._segmentDeltaX;
            const intersectionY = this._p1Y + segmentRatio * this._segmentDeltaY;
            deltaX = x - intersectionX;
            deltaY = y - intersectionY;
            return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        }
    }

    /**
     * Distance alongside the segment (starting at P1) to the interection point (point on the segment perpendicular to the specified point)
     * @public
     * @param {Number} x
     * @param {Number} y
     * @returns {Number}
     */
    intersectionDistance(x, y) {
        // Segment is a single point
        if (this._length === 0) {
            return 0;
        }

        // Segment ratio : 0 -> 1 ratio on the line segment perpendicular to the specified point
        const deltaX = x - this._p1X;
        const deltaY = y - this._p1Y;
        const segmentRatio = (deltaX * this._segmentDeltaX + deltaY * this._segmentDeltaY) * this._inverseSquareLength;

        return segmentRatio * this._length;
    }
}
